import { useState } from 'react';
import { motion } from 'motion/react';
import {
  X,
  Settings,
  Bell,
  Moon,
  Sun,
  Shield,
  Trash2,
  HelpCircle,
  FileText,
  Mail,
  ExternalLink,
  ChevronRight,
  Palette,
  Volume2,
  VolumeX,
  Eye,
  EyeOff,
  Smartphone,
  Cloud,
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { useUIStore } from '@/stores/ui-store';
import { useNotificationsStore } from '@/stores/notifications-store';
import { useCapturesStore } from '@/stores/captures-store';

interface SettingsSectionProps {
  title: string;
  children: React.ReactNode;
}

function SettingsSection({ title, children }: SettingsSectionProps) {
  return (
    <div className="mb-6">
      <h3 className="text-xs text-white/40 uppercase tracking-wider font-semibold mb-3 px-1">
        {title}
      </h3>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

interface SettingsItemProps {
  icon: React.ElementType;
  iconColor?: string;
  iconBg?: string;
  label: string;
  description?: string;
  value?: string | React.ReactNode;
  onClick?: () => void;
  danger?: boolean;
}

function SettingsItem({
  icon: Icon,
  iconColor = 'text-white/70',
  iconBg = 'bg-white/10',
  label,
  description,
  value,
  onClick,
  danger,
}: SettingsItemProps) {
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      className={`w-full flex items-center gap-4 p-3.5 rounded-2xl transition-colors ${
        onClick ? 'hover:bg-white/[0.06] cursor-pointer' : 'cursor-default'
      } ${danger ? 'hover:bg-red-500/10' : ''}`}
    >
      <div className={`w-10 h-10 rounded-full ${iconBg} flex items-center justify-center flex-shrink-0`}>
        <Icon size={20} className={danger ? 'text-red-400' : iconColor} />
      </div>
      <div className="flex-1 text-left">
        <p className={`font-medium ${danger ? 'text-red-400' : ''}`}>{label}</p>
        {description && <p className="text-xs text-white/40 mt-0.5">{description}</p>}
      </div>
      {value && <div className="text-sm text-white/50">{value}</div>}
      {onClick && !value && <ChevronRight size={18} className="text-white/30" />}
    </button>
  );
}

interface ToggleProps {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
}

function Toggle({ enabled, onChange }: ToggleProps) {
  return (
    <button
      onClick={() => onChange(!enabled)}
      className={`w-12 h-7 rounded-full p-1 transition-colors ${
        enabled ? 'bg-accent-blue' : 'bg-white/20'
      }`}
    >
      <motion.div
        animate={{ x: enabled ? 20 : 0 }}
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        className="w-5 h-5 rounded-full bg-white shadow-md"
      />
    </button>
  );
}

export default function SettingsDrawer({ onClose }: { onClose: () => void }) {
  const { user, logout } = useAuthStore();
  const { showToast } = useUIStore();
  const { clearAll: clearNotifications } = useNotificationsStore();
  const { clearAllCaptures } = useCapturesStore();

  // Settings state (would normally be persisted)
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [darkMode, setDarkMode] = useState(true);
  const [autoSaveCaptures, setAutoSaveCaptures] = useState(true);
  const [highQualityAR, setHighQualityAR] = useState(true);

  const handleClearAllData = () => {
    if (window.confirm('Are you sure you want to clear all local data? This cannot be undone.')) {
      clearNotifications();
      clearAllCaptures();
      localStorage.clear();
      showToast('All local data cleared', 'success');
    }
  };

  const handleDeleteAccount = () => {
    if (
      window.confirm(
        'Are you sure you want to delete your account? This action is permanent and cannot be undone.'
      )
    ) {
      showToast('Account deletion is not available in beta', 'info');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100]"
    >
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
      />

      {/* Drawer */}
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        className="absolute bottom-0 left-0 right-0 max-h-[90vh] bg-background rounded-t-[32px] overflow-hidden flex flex-col"
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 rounded-full bg-white/20" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-6 pb-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
              <Settings size={20} className="text-white/70" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Settings</h2>
              <p className="text-sm text-white/50">Customize your experience</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2.5 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Settings Content */}
        <div className="flex-1 overflow-y-auto no-scrollbar px-6 py-4">
          {/* Notifications */}
          <SettingsSection title="Notifications">
            <SettingsItem
              icon={Bell}
              iconColor="text-accent-blue"
              iconBg="bg-accent-blue/15"
              label="Push Notifications"
              description="Receive alerts for favorites and captures"
              value={<Toggle enabled={notificationsEnabled} onChange={setNotificationsEnabled} />}
            />
            <SettingsItem
              icon={soundEnabled ? Volume2 : VolumeX}
              iconColor="text-accent-purple"
              iconBg="bg-accent-purple/15"
              label="Sound Effects"
              description="Play sounds for captures and actions"
              value={<Toggle enabled={soundEnabled} onChange={setSoundEnabled} />}
            />
          </SettingsSection>

          {/* Appearance */}
          <SettingsSection title="Appearance">
            <SettingsItem
              icon={darkMode ? Moon : Sun}
              iconColor="text-yellow-400"
              iconBg="bg-yellow-500/15"
              label="Dark Mode"
              description="Always enabled in this version"
              value={<Toggle enabled={darkMode} onChange={setDarkMode} />}
            />
            <SettingsItem
              icon={Palette}
              iconColor="text-pink-400"
              iconBg="bg-pink-500/15"
              label="Accent Color"
              description="Customize your theme"
              value="Blue"
              onClick={() => showToast('Theme customization coming soon', 'info')}
            />
          </SettingsSection>

          {/* AR Settings */}
          <SettingsSection title="AR Experience">
            <SettingsItem
              icon={Eye}
              iconColor="text-green-400"
              iconBg="bg-green-500/15"
              label="High Quality AR"
              description="Better visuals, more battery usage"
              value={<Toggle enabled={highQualityAR} onChange={setHighQualityAR} />}
            />
            <SettingsItem
              icon={Cloud}
              iconColor="text-accent-blue"
              iconBg="bg-accent-blue/15"
              label="Auto-Save Captures"
              description="Automatically save AR photos"
              value={<Toggle enabled={autoSaveCaptures} onChange={setAutoSaveCaptures} />}
            />
          </SettingsSection>

          {/* Privacy & Security */}
          <SettingsSection title="Privacy & Security">
            <SettingsItem
              icon={Shield}
              iconColor="text-accent-purple"
              iconBg="bg-accent-purple/15"
              label="Privacy Policy"
              value={<ExternalLink size={16} className="text-white/30" />}
              onClick={() => showToast('Opening privacy policy...', 'info')}
            />
            <SettingsItem
              icon={FileText}
              iconColor="text-white/70"
              iconBg="bg-white/10"
              label="Terms of Service"
              value={<ExternalLink size={16} className="text-white/30" />}
              onClick={() => showToast('Opening terms of service...', 'info')}
            />
          </SettingsSection>

          {/* Support */}
          <SettingsSection title="Support">
            <SettingsItem
              icon={HelpCircle}
              iconColor="text-accent-blue"
              iconBg="bg-accent-blue/15"
              label="Help Center"
              onClick={() => showToast('Opening help center...', 'info')}
            />
            <SettingsItem
              icon={Mail}
              iconColor="text-green-400"
              iconBg="bg-green-500/15"
              label="Contact Support"
              description="Get help from our team"
              onClick={() => showToast('Opening email client...', 'info')}
            />
          </SettingsSection>

          {/* Data & Storage */}
          <SettingsSection title="Data & Storage">
            <SettingsItem
              icon={Smartphone}
              iconColor="text-white/70"
              iconBg="bg-white/10"
              label="Clear Cache"
              description="Free up storage space"
              onClick={() => {
                showToast('Cache cleared', 'success');
              }}
            />
            <SettingsItem
              icon={Trash2}
              iconBg="bg-red-500/10"
              label="Clear All Local Data"
              description="Remove all cached data and preferences"
              onClick={handleClearAllData}
              danger
            />
          </SettingsSection>

          {/* Danger Zone */}
          {user && (
            <SettingsSection title="Account">
              <SettingsItem
                icon={Trash2}
                iconBg="bg-red-500/10"
                label="Delete Account"
                description="Permanently delete your account and data"
                onClick={handleDeleteAccount}
                danger
              />
            </SettingsSection>
          )}

          {/* App Info */}
          <div className="text-center py-6 border-t border-white/10 mt-4">
            <p className="text-white/30 text-sm font-medium">VisionAR</p>
            <p className="text-white/20 text-xs mt-1">Version 1.0.0 (Beta)</p>
          </div>
        </div>

        {/* Safe Area Bottom */}
        <div className="safe-area-bottom" />
      </motion.div>
    </motion.div>
  );
}
